import {
  Button,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { ProcessChoice, ProcessWorkflow } from "../../api";
import RoleSlots from "./RoleSlots";
import { labelFor, WORKFLOW_TYPE_LABEL } from "./terms";
import type { FieldRef } from "./types";

const { Text } = Typography;

type Props = {
  workflow: ProcessWorkflow;
  onChange: (patch: Partial<ProcessWorkflow>) => void;
  onRemove?: () => void;
  assignments: ProcessChoice[];
  workflowTypes: ProcessChoice[];
  groups: Array<{ id: number; name: string }>;
  /** Passed straight through to the per-slot rule builder; see RoleSlots. */
  fields?: FieldRef[];
  ops?: string[];
  orderOps?: string[];
  orgUnits?: Array<{ id: number; code: string; name: string; has_head: boolean }>;
  derivedAssignments?: string[];
  stageIndex?: number;
  disabled?: boolean;
};

/**
 * One sign-off: what it is called, how it is signed, and by whom.
 *
 * This used to call a sign-off a "step", which is now what a *stage* is called
 * on the canvas -- the collision is exactly the vocabulary problem the designer
 * had. A sign-off is one signing duty *inside* a step; a step may hold several.
 *
 * `approvers_count` and named slots are two different ways of gating the same
 * sign-off, and only one of them applies. With slots, each named duty must be
 * signed; without them it completes on a flat count. Showing both at once
 * invites an admin to set a count that is then quietly ignored, so the count is
 * hidden as soon as a slot exists.
 */
const WorkflowEditor = ({
  workflow,
  onChange,
  onRemove,
  assignments,
  workflowTypes,
  groups,
  fields,
  ops,
  orderOps,
  orgUnits,
  derivedAssignments,
  stageIndex,
  disabled,
}: Props) => {
  const { t } = useTranslation();
  const hasSlots = (workflow.roles?.length ?? 0) > 0;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("What is this sign-off called?")}
        </Text>
        <Input
          style={{ marginTop: 4 }}
          value={workflow.name}
          disabled={disabled}
          status={workflow.name.trim() ? undefined : "error"}
          placeholder={t("e.g. Permit Issuer")}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </div>

      <div>
        <Tooltip
          title={t(
            "A digital certificate or a drawn signature cannot be given by clicking a link, so only 'a click in the app' can be approved straight from an email."
          )}
        >
          <Text
            type="secondary"
            style={{ fontSize: 12, borderBottom: "1px dotted" }}
          >
            {t("How is it signed?")}
          </Text>
        </Tooltip>
        <Select
          style={{ width: "100%", marginTop: 4 }}
          value={workflow.type}
          disabled={disabled}
          onChange={(v) => onChange({ type: v })}
          options={workflowTypes.map((w) => ({
            value: w.value,
            label: t(labelFor(WORKFLOW_TYPE_LABEL, w.value)),
          }))}
        />
      </div>

      {!hasSlots && (
        <div>
          <Tooltip
            title={t(
              "With no named slots, the sign-off finishes once this many people have approved — whoever they are."
            )}
          >
            <Text
              type="secondary"
              style={{ fontSize: 12, borderBottom: "1px dotted" }}
            >
              {t("How many approvals does it take?")}
            </Text>
          </Tooltip>
          <div style={{ marginTop: 4 }}>
            <InputNumber
              min={1}
              style={{ width: 90 }}
              value={workflow.approvers_count}
              disabled={disabled}
              onChange={(v) => onChange({ approvers_count: Number(v) || 1 })}
            />
          </div>
        </div>
      )}

      {hasSlots && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "This sign-off has named slots, so every slot below has to be signed. The plain approval count no longer applies."
          )}
        </Text>
      )}

      <RoleSlots
        roles={workflow.roles}
        onChange={(roles) => onChange({ roles })}
        assignments={assignments}
        groups={groups}
        fields={fields}
        ops={ops}
        orderOps={orderOps}
        orgUnits={orgUnits}
        derivedAssignments={derivedAssignments}
        stageIndex={stageIndex}
        disabled={disabled}
      />

      {onRemove &&
        (workflow.in_use ? (
          <Tooltip
            title={t(
              "Somebody is signing this right now, so it cannot be removed. Take it out of its step instead."
            )}
          >
            <Tag>{t("Being signed right now")}</Tag>
          </Tooltip>
        ) : (
          <Popconfirm
            title={t("Remove this sign-off?")}
            description={t(
              "It stays on record for cases already signed. Nothing in progress is affected."
            )}
            okText={t("Remove")}
            cancelText={t("Cancel")}
            onConfirm={onRemove}
          >
            <Button
              size="small"
              danger
              disabled={disabled}
              icon={<DeleteOutlined />}
            >
              {t("Remove this sign-off")}
            </Button>
          </Popconfirm>
        ))}
    </Space>
  );
};

export default WorkflowEditor;
